import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { LogOut, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * Sticky red banner shown while a platform_owner is impersonating a
 * tenant's owner. Visible across every authenticated screen so the
 * founder can never forget who they're acting as. One click → swap
 * back to the original platform_owner token and return to the
 * Tenants Console.
 */
const ImpersonationBanner = () => {
  const { user, exitImpersonation, isImpersonating } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating || !user) return null;

  const firm = user.impersonated_firm || user.tenant_id || "this tenant";
  const target = user.email || user.name || "their owner";

  const handleExit = async () => {
    setExiting(true);
    try {
      await exitImpersonation();
      toast.success("Returned to your platform-owner account");
      // Hard navigation so the entire app re-mounts under the
      // platform_owner token and re-fetches /auth/me from scratch.
      window.location.replace("/platform/tenants");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to exit impersonation");
    } finally {
      setExiting(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-[60] w-full"
      style={{ background: "linear-gradient(90deg, #7f1d1d 0%, #991b1b 60%, #b91c1c 100%)" }}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center justify-between px-4 py-2 text-white text-sm gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert size={18} className="flex-shrink-0 text-amber-200" />
          <div className="truncate">
            <span className="font-semibold uppercase tracking-wide text-amber-200 mr-2">IMPERSONATING</span>
            <span className="font-medium">{firm}</span>
            <span className="text-white/75"> &middot; acting as </span>
            <span className="font-mono text-xs">{target}</span>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="bg-white/95 text-red-800 border-white hover:bg-white"
          onClick={handleExit}
          disabled={exiting}
          data-testid="exit-impersonation-button"
        >
          <LogOut size={14} className="mr-1.5" />
          {exiting ? "Exiting…" : "Exit impersonation"}
        </Button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
